<div class="mod-page">
  <app-project-nav></app-project-nav>
  <app-page-header [title]="'modules.pageTitle' | translate" [subtitle]="'modules.pageSubtitle' | translate">
    <ng-container right>
      <app-stat-group
        class="mod-header-stats"
        role="group"
        [items]="statItems()"
        [attr.aria-label]="statsAriaLabel()"
      />
      @if (!isProjectReader()) {
        <button mat-flat-button color="primary" type="button" class="mod-create-btn" (click)="onCreate()">
          <mat-icon aria-hidden="true">add</mat-icon>
          {{ 'modules.addButton' | translate }}
        </button>
      }
    </ng-container>
  </app-page-header>

  <div class="mod-toolbar">
    <app-search-bar
      class="mod-search"
      [placeholder]="'modules.searchPlaceholder' | translate"
      [value]="searchTerm()"
      (valueChange)="onSearchChange($event)"
    />
    <app-module-filter-bar
      [statusOptions]="statusOptions()"
      [authorOptions]="authorOptions()"
      [selectedStatus]="selectedStatus()"
      [selectedAuthor]="selectedAuthor()"
      [hasActiveFilters]="hasActiveFilters()"
      [savedViews]="savedViewsOptions()"
      [activeSavedView]="activeSavedViewName()"
      [projectId]="projectId"
      (filterChange)="onFilterChange($event)"
      (resetFilters)="onResetFilters()"
      (savedViewSelected)="onSavedViewSelected($event)"
      (savedViewSaved)="onSavedViewSaved($event)"
      (savedViewDeleted)="onSavedViewDeleted($event)"
      (savedViewDeleteFailed)="onSavedViewDeleteFailed()"
    />
    <span class="mod-result-count">
      {{ resultCountKey() | translate: { count: total(), total: unfilteredTotal() } }}
    </span>
  </div>

  @if (deleteError()) {
    <p class="mod-hint mod-hint--error" role="alert" aria-live="polite">{{ deleteErrorKey() | translate }}</p>
  }

  @if (savedViewsError()) {
    <p class="mod-hint mod-hint--error" role="alert" aria-live="polite">{{ savedViewsError() }}</p>
  }

  @if (loading()) {
    <div class="mod-loading">
      <mat-spinner diameter="32" />
    </div>
  } @else if (loadError()) {
    <p class="mod-hint mod-hint--error" role="alert">{{ 'modules.loadError' | translate }}</p>
  } @else if (rows().length === 0) {
    <p class="mod-hint">{{ 'modules.empty' | translate }}</p>
  } @else if (!hasResults()) {
    <p class="mod-hint mod-hint--filtered">{{ 'modules.emptyFiltered' | translate }}</p>
  } @else {
    <div appListFocusScroll class="mod-card" role="table" [attr.aria-label]="'modules.tableLabel' | translate">
      <div class="mod-table-header" role="row">
        <div class="mod-col mod-col--id" role="columnheader" [attr.aria-sort]="sortAria('displayId')">
          <button type="button" class="mod-sort-btn" (click)="sortBy('displayId')">
            {{ 'modules.table.id' | translate }}
            <mat-icon
              class="mod-sort-btn__icon"
              [class.mod-sort-btn__icon--active]="tableSort().col === 'displayId'"
              aria-hidden="true"
            >
              {{ sortIcon('displayId') }}
            </mat-icon>
          </button>
        </div>
        <div class="mod-col mod-col--title" role="columnheader" [attr.aria-sort]="sortAria('title')">
          <button type="button" class="mod-sort-btn" (click)="sortBy('title')">
            {{ 'modules.table.title' | translate }}
            <mat-icon
              class="mod-sort-btn__icon"
              [class.mod-sort-btn__icon--active]="tableSort().col === 'title'"
              aria-hidden="true"
            >
              {{ sortIcon('title') }}
            </mat-icon>
          </button>
        </div>
        <div class="mod-col mod-col--steps" role="columnheader" [attr.aria-sort]="sortAria('stepCount')">
          <button type="button" class="mod-sort-btn" (click)="sortBy('stepCount')">
            {{ 'modules.table.steps' | translate }}
            <mat-icon
              class="mod-sort-btn__icon"
              [class.mod-sort-btn__icon--active]="tableSort().col === 'stepCount'"
              aria-hidden="true"
            >
              {{ sortIcon('stepCount') }}
            </mat-icon>
          </button>
        </div>
        <div class="mod-col mod-col--usage" role="columnheader" [attr.aria-sort]="sortAria('usageCount')">
          <button type="button" class="mod-sort-btn" (click)="sortBy('usageCount')">
            {{ 'modules.table.usage' | translate }}
            <mat-icon
              class="mod-sort-btn__icon"
              [class.mod-sort-btn__icon--active]="tableSort().col === 'usageCount'"
              aria-hidden="true"
            >
              {{ sortIcon('usageCount') }}
            </mat-icon>
          </button>
        </div>
        <div class="mod-col mod-col--status" role="columnheader" [attr.aria-sort]="sortAria('status')">
          <button type="button" class="mod-sort-btn" (click)="sortBy('status')">
            {{ 'modules.table.status' | translate }}
            <mat-icon
              class="mod-sort-btn__icon"
              [class.mod-sort-btn__icon--active]="tableSort().col === 'status'"
              aria-hidden="true"
            >
              {{ sortIcon('status') }}
            </mat-icon>
          </button>
        </div>
        <div class="mod-col mod-col--author" role="columnheader" [attr.aria-sort]="sortAria('authorName')">
          <button type="button" class="mod-sort-btn" (click)="sortBy('authorName')">
            {{ 'modules.table.author' | translate }}
            <mat-icon
              class="mod-sort-btn__icon"
              [class.mod-sort-btn__icon--active]="tableSort().col === 'authorName'"
              aria-hidden="true"
            >
              {{ sortIcon('authorName') }}
            </mat-icon>
          </button>
        </div>
        <div class="mod-col mod-col--actions" role="columnheader">
          {{ 'modules.table.actions' | translate }}
        </div>
      </div>

      @for (row of pagedRows(); track row.id; let last = $last) {
        <div
          class="mod-row"
          [class.mod-row--last]="last"
          role="row"
          tabindex="0"
          [attr.aria-label]="row.title"
          (click)="onEdit(row)"
          (keydown.enter)="onRowKeydown($event, row)"
        >
          <div class="mod-col mod-col--id" role="cell">
            <app-code-id-chip [id]="row.displayId" />
          </div>
          <div class="mod-col mod-col--title" role="cell">
            <span class="mod-title">{{ row.title }}</span>
            @if (row.description) {
              <span class="mod-desc">{{ row.description }}</span>
            }
          </div>
          <div class="mod-col mod-col--steps" role="cell">
            <span class="mod-count" [attr.aria-label]="'modules.stepsAria' | translate: { count: row.stepCount }">
              <span class="mod-count__value">{{ 'modules.stepsValue' | translate: { count: row.stepCount } }}</span>
              <span class="mod-count__unit">{{ 'modules.stepsUnit' | translate }}</span>
            </span>
          </div>
          <div class="mod-col mod-col--usage" role="cell">
            <span class="mod-usage" [attr.aria-label]="'modules.usageAria' | translate: { count: row.usageCount }">
              {{ 'modules.usageValue' | translate: { count: row.usageCount } }} {{ 'modules.usageUnit' | translate }}
            </span>
          </div>
          <div class="mod-col mod-col--status" role="cell">
            <app-lifecycle-pill [kind]="row.statusKind" [label]="row.statusLabel" />
          </div>
          <div class="mod-col mod-col--author" role="cell">
            @if (row.authorName) {
              <app-avatar [name]="row.authorName" [size]="28" aria-hidden="true" />
              <span class="mod-author__name">{{ row.authorName }}</span>
            } @else {
              <span class="mod-none" [attr.aria-label]="'modules.table.noAuthor' | translate">—</span>
            }
          </div>
          <div class="mod-col mod-col--actions" role="cell">
            @if (!isProjectReader()) {
              <button
                type="button"
                class="mod-action-btn mod-action-btn--accent"
                [attr.aria-label]="'modules.edit' | translate"
                (click)="$event.stopPropagation(); onEdit(row)"
              >
                <mat-icon aria-hidden="true">edit</mat-icon>
              </button>
              <button
                type="button"
                class="mod-action-btn mod-action-btn--danger"
                [attr.aria-label]="'modules.delete' | translate"
                (click)="$event.stopPropagation(); onDeleteRequest(row)"
              >
                <mat-icon aria-hidden="true">delete</mat-icon>
              </button>
            }
          </div>
        </div>
      }
    </div>

    <div class="mod-footer">
      <span class="mod-footer__range">
        {{ 'modules.footer.rangeLabel' | translate: { start: rangeStart(), end: rangeEnd(), total: total() } }}
      </span>
      <div class="mod-footer__pager">
        <select
          id="mod-page-size"
          class="mod-footer__select"
          (change)="onPageSizeChange(+pageSizeSelect.value)"
          #pageSizeSelect
          [attr.aria-label]="'modules.footer.rowsPerPageAriaLabel' | translate"
        >
          @for (size of pageSizeOptions; track size) {
            <option [value]="size" [selected]="pageSize() === size">{{ size }}</option>
          }
        </select>
        <div class="mod-footer__nav">
          <button
            type="button"
            class="mod-action-btn"
            [disabled]="pageIndex() === 0"
            [attr.aria-label]="'modules.footer.previousPage' | translate"
            (click)="onPageChange(pageIndex() - 1)"
          >
            <mat-icon aria-hidden="true">chevron_left</mat-icon>
          </button>
          <button
            type="button"
            class="mod-action-btn"
            [disabled]="pageIndex() >= pageCount() - 1"
            [attr.aria-label]="'modules.footer.nextPage' | translate"
            (click)="onPageChange(pageIndex() + 1)"
          >
            <mat-icon aria-hidden="true">chevron_right</mat-icon>
          </button>
        </div>
      </div>
    </div>
  }
</div>

<app-confirm-dialog
  [visible]="showDeleteModal()"
  [title]="'modules.deleteTitle' | translate"
  [message]="deleteMessage()"
  [confirmLabel]="'modules.deleteConfirm' | translate"
  [cancelLabel]="'modules.deleteCancel' | translate"
  [busy]="deleting()"
  (confirmed)="onConfirmDelete()"
  (cancelled)="onCancelDelete()"
/>
